import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { get, post } from '@/services/apiClient';

interface LineEvent {
  id: number;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  user_id: string;
  event_type: string;
  msg_type?: string;
  text?: string;
}

interface BotInfo {
  userId: string;
  basicId: string;
  displayName: string;
  pictureUrl?: string;
  chatMode: string;
  markAsReadMode: string;
}

interface UserProfile {
  user_id: string;
  display_name: string;
  picture_url?: string;
  status_message?: string;
}

const POLL_INTERVAL = 3000;

const LineChatMonitor: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { isAuthenticated, user } = useAuth();
  const [events, setEvents] = useState<LineEvent[]>([]);
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [followers, setFollowers] = useState<UserProfile[]>([]);
  const [showUsers, setShowUsers] = useState(true);
  const [targetUserId, setTargetUserId] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const lastEventId = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  // TODO: deprecated — replace with role-based check from JWT when Google OAuth is used in production
  const isAdmin = user?.username === 'derick' || user?.username === 'paul' || user?.username === 'april';

  // Fetch bot info and followers on mount
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    get<BotInfo>('/api/v1/line/bot')
      .then(setBotInfo)
      .catch(() => setBotInfo(null));
    get<UserProfile[]>('/api/v1/line/followers')
      .then(setFollowers)
      .catch(() => setFollowers([]));
  }, [isAuthenticated, isAdmin]);

  // Poll for new events
  const fetchEvents = useCallback(async () => {
    try {
      const data = await get<LineEvent[]>(`/api/v1/line/events?after=${lastEventId.current}`);
      if (data.length > 0) {
        setEvents((prev) => [...prev, ...data]);
        lastEventId.current = data[data.length - 1].id;

        // Auto-set target user from first inbound event
        const firstInbound = data.find((e) => e.direction === 'inbound' && e.user_id);
        if (firstInbound && !targetUserId) {
          setTargetUserId(firstInbound.user_id);
        }
      }
    } catch {
      // Silently ignore poll errors
    }
  }, [targetUserId]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    fetchEvents();
    pollRef.current = setInterval(fetchEvents, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [isAuthenticated, isAdmin, fetchEvents]);

  // Auto-scroll to bottom on new events
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const handleSend = async () => {
    if (!targetUserId.trim() || !messageText.trim()) return;
    setSending(true);
    setError(null);
    try {
      await post('/api/v1/line/test', {
        user_id: targetUserId.trim(),
        message: messageText.trim(),
      });
      setMessageText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500 dark:text-gray-400">{t('line.loginRequired', 'Please log in as admin to use LINE Monitor')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header with bot info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            {botInfo?.pictureUrl ? (
              <img src={botInfo.pictureUrl} alt="LINE Bot" className="w-12 h-12 rounded-full" />
            ) : (
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 5.82 2 10.5c0 4.12 3.53 7.57 8.29 8.35.32.07.76.21.87.48.1.25.07.63.03.88l-.14.84c-.04.25-.2.97.85.53s5.6-3.3 7.64-5.65C21.46 13.64 22 12.13 22 10.5 22 5.82 17.52 2 12 2z" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
              {botInfo?.displayName || t('line.title', 'LINE Chat Monitor')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {botInfo ? botInfo.basicId : t('line.connecting', 'Connecting...')}
            </p>
          </div>
          {botInfo?.basicId && (
            <button
              onClick={() => setShowQR(!showQR)}
              className={`p-2 rounded-lg transition-colors ${
                showQR
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('line.showQR', 'Show QR Code')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </button>
          )}
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            botInfo ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500'
          }`}>
            {botInfo ? t('line.online', 'Online') : t('line.offline', 'Offline')}
          </div>
        </div>

        {/* QR Code Panel */}
        {showQR && botInfo?.basicId && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex flex-col items-center gap-3">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://line.me/R/ti/p/${botInfo.basicId}`)}`}
              alt="LINE QR Code"
              className="w-48 h-48 rounded-lg"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('line.scanToAdd', 'Scan to add as friend')}
            </p>
            <a
              href={`https://line.me/R/ti/p/${botInfo.basicId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              line.me/R/ti/p/{botInfo.basicId}
            </a>
          </div>
        )}
      </div>

      {/* Main layout: Users panel + Chat area */}
      <div className="flex gap-4" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
        {/* Users panel */}
        {showUsers && (
          <div className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('line.followers', 'Followers')} ({followers.length})
              </h2>
              <button
                onClick={() => {
                  get<UserProfile[]>('/api/v1/line/followers')
                    .then(setFollowers)
                    .catch(() => {});
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title={t('line.refresh', 'Refresh')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {followers.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">
                  {t('line.noFollowers', 'No followers yet')}
                </div>
              ) : (
                followers.map((follower) => (
                  <button
                    key={follower.user_id}
                    onClick={() => setTargetUserId(follower.user_id)}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left ${
                      targetUserId === follower.user_id ? 'bg-green-50 dark:bg-green-900/20 border-l-2 border-green-500' : ''
                    }`}
                  >
                    {follower.picture_url ? (
                      <img
                        src={follower.picture_url}
                        alt={follower.display_name}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {follower.display_name}
                      </p>
                      {follower.status_message && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {follower.status_message}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Chat area */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col min-w-0">
          {/* Toggle users panel button */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-3 py-2">
            <button
              onClick={() => setShowUsers(!showUsers)}
              className={`p-1.5 rounded-lg transition-colors ${
                showUsers ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('line.toggleUsers', 'Toggle users panel')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
            {targetUserId && (
              <div className="ml-3 flex items-center gap-2">
                <span className="text-xs text-gray-400">{t('line.to', 'To')}:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {followers.find(f => f.user_id === targetUserId)?.display_name || targetUserId.slice(0, 16) + '...'}
                </span>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {events.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>{t('line.noEvents', 'No events yet. Send a message to your LINE Official Account to see it here.')}</p>
                </div>
              </div>
            ) : (
              events.map((event, idx) => {
                const showDate = idx === 0 || formatDate(events[idx - 1].timestamp) !== formatDate(event.timestamp);
                const senderProfile = followers.find(f => f.user_id === event.user_id);
                return (
                  <div key={event.id}>
                    {showDate && (
                      <div className="flex justify-center my-2">
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-full">
                          {formatDate(event.timestamp)}
                        </span>
                      </div>
                    )}
                    <div className={`flex ${event.direction === 'outbound' ? 'justify-end' : 'justify-start'} gap-2`}>
                      {/* Inbound avatar */}
                      {event.direction === 'inbound' && (
                        <button
                          onClick={() => setTargetUserId(event.user_id)}
                          className="flex-shrink-0 self-end"
                          title={senderProfile?.display_name || event.user_id}
                        >
                          {senderProfile?.picture_url ? (
                            <img src={senderProfile.picture_url} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                        </button>
                      )}

                      <div className={`max-w-[60%] ${
                        event.direction === 'outbound'
                          ? 'bg-green-500 text-white rounded-l-xl rounded-tr-xl'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-r-xl rounded-tl-xl'
                      } px-4 py-2 shadow-sm`}>
                        {/* Sender name for inbound */}
                        {event.direction === 'inbound' && (
                          <p className="text-xs text-blue-500 dark:text-blue-400 mb-1 font-medium">
                            {senderProfile?.display_name || event.user_id.slice(0, 12) + '...'}
                          </p>
                        )}

                        {/* Event content */}
                        {event.event_type === 'message' ? (
                          event.msg_type === 'text' ? (
                            <p className="text-sm whitespace-pre-wrap break-words">{event.text}</p>
                          ) : (
                            <p className="text-sm italic opacity-75">[{event.msg_type}]</p>
                          )
                        ) : (
                          <p className="text-sm italic opacity-75">{event.text || `[${event.event_type}]`}</p>
                        )}

                        <p className={`text-xs mt-1 ${
                          event.direction === 'outbound' ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {formatTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Error display */}
          {error && (
            <div className="mx-4 mb-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Message input */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-end gap-2">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  targetUserId
                    ? t('line.inputPlaceholder', 'Type a message...')
                    : t('line.selectUser', 'Select a user to message')
                }
                disabled={!targetUserId}
                rows={1}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={sending || !messageText.trim() || !targetUserId.trim()}
                className="px-4 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineChatMonitor;
